import React from 'react';
import { CreditCard, Building2, QrCode, Banknote, Settings2, Plus, Edit2, Trash2, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';

export const SettingsPayments = ({
  settings, setSettings, handleSave,
  mpConnectionStatus, setShowMPConfig,
  bankAccounts, setShowAddBankAccount, setEditingBankAccount, handleDeleteBankAccount
}) => (
  <div className="space-y-6">
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Medios de Pago Globales</h3>
          <p className="text-sm text-slate-600">Configuración por defecto para todas las carteras. Las carteras pueden tener su propia configuración.</p>
        </div>
      </div>
      <div className="space-y-4">
        {/* Mercado Pago */}
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center"><CreditCard className="w-6 h-6 text-blue-600" /></div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium text-slate-900">Mercado Pago</p>
                  {mpConnectionStatus === 'connected' && <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Conectado</Badge>}
                  {mpConnectionStatus === 'disconnected' && <Badge className="bg-red-100 text-red-700 flex items-center gap-1"><XCircle className="w-3 h-3" />No conectado</Badge>}
                  {mpConnectionStatus === 'checking' && <Badge className="bg-slate-100 text-slate-700 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Verificando...</Badge>}
                </div>
                <p className="text-sm text-slate-500">Pagos con tarjeta, débito y saldo MP</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={() => setShowMPConfig(true)} data-testid="configure-mercadopago"><Settings2 className="w-4 h-4 mr-1" />Configurar</Button>
              <Switch checked={settings.payment_methods?.mercadopago || false} onCheckedChange={(checked) => setSettings({...settings, payment_methods: { ...settings.payment_methods, mercadopago: checked }})} data-testid="toggle-mercadopago" />
            </div>
          </div>
        </div>

        {/* Transferencia Bancaria */}
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center"><Building2 className="w-6 h-6 text-emerald-600" /></div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium text-slate-900">Transferencia Bancaria</p>
                  {bankAccounts.length > 0 && <Badge className="bg-emerald-100 text-emerald-700">{bankAccounts.length} cuenta{bankAccounts.length > 1 ? 's' : ''}</Badge>}
                </div>
                <p className="text-sm text-slate-500">Muestra datos de CBU/Alias para transferir</p>
              </div>
            </div>
            <Switch checked={settings.payment_methods?.transfer || false} onCheckedChange={(checked) => setSettings({...settings, payment_methods: { ...settings.payment_methods, transfer: checked }})} data-testid="toggle-transfer" />
          </div>
          {settings.payment_methods?.transfer && (
            <div className="p-4 bg-slate-50 border-t border-slate-200 space-y-4">
              {bankAccounts.length === 0 ? (
                <div className="text-center py-4"><Building2 className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-500 text-sm">No hay cuentas bancarias configuradas</p></div>
              ) : (
                <div className="space-y-3">
                  {bankAccounts.map((account) => (
                    <div key={account.id} className={`p-3 bg-white rounded-lg border ${account.is_default ? 'border-emerald-300 ring-1 ring-emerald-100' : 'border-slate-200'}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-slate-900">{account.bank}</p>
                            {account.is_default && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Principal</Badge>}
                            <Badge variant="outline" className="text-xs">{account.account_type === 'caja_ahorro' ? 'Caja de Ahorro' : 'Cuenta Corriente'}</Badge>
                          </div>
                          <p className="text-sm text-slate-600">{account.holder}</p>
                          <p className="text-xs text-slate-500 font-mono mt-1">CBU: {account.cbu}{account.alias && <span className="ml-2">| Alias: {account.alias}</span>}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setEditingBankAccount(account)} data-testid={`edit-bank-${account.id}`}><Edit2 className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => handleDeleteBankAccount(account.id)} data-testid={`delete-bank-${account.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Button variant="outline" className="w-full" onClick={() => setShowAddBankAccount(true)} data-testid="add-bank-account-btn"><Plus className="w-4 h-4 mr-2" />Agregar Cuenta Bancaria</Button>
            </div>
          )}
        </div>

        {/* Upcoming payment methods */}
        {[
          { name: 'Modo', icon: QrCode, color: 'violet', desc: 'Pagos con QR y billeteras virtuales' },
          { name: 'Pago Fácil', icon: Banknote, color: 'orange', desc: 'Pago en efectivo en sucursales' },
          { name: 'Rapipago', icon: Banknote, color: 'red', desc: 'Pago en efectivo en sucursales' }
        ].map(({ name, icon: Icon, color, desc }) => (
          <div key={name} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg bg-slate-50 opacity-70">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 bg-${color}-100 rounded-lg flex items-center justify-center`}><Icon className={`w-6 h-6 text-${color}-600`} /></div>
              <div>
                <p className="font-medium text-slate-900 flex items-center gap-2">{name}<Badge className="bg-amber-100 text-amber-700">PRÓXIMO</Badge></p>
                <p className="text-sm text-slate-500">{desc}</p>
              </div>
            </div>
            <Switch disabled checked={false} />
          </div>
        ))}
      </div>
      <div className="mt-6 pt-6 border-t border-slate-200">
        <Button onClick={handleSave} className="bg-teal-600 hover:bg-teal-700">Guardar Configuración de Pagos</Button>
      </div>
    </Card>

    <Card className="p-4 bg-blue-50 border-blue-200">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0"><Settings2 className="w-5 h-5 text-blue-600" /></div>
        <div>
          <p className="font-medium text-blue-900">Configuración por Cartera</p>
          <p className="text-sm text-blue-700 mt-1">Puedes personalizar los medios de pago para cada cartera desde el panel de <strong>Carteras</strong>. Si una cartera no tiene configuración propia, heredará esta configuración global.</p>
        </div>
      </div>
    </Card>
  </div>
);
